import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Button, Stack, Divider } from '@mui/material';
import appStoreBadge from '../assets/app-store-badge.svg';
import playStoreBadge from '../assets/play-store-badge.png';

export default function InstallRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const campaign = params.get('campaign') || '';

  const [platform, setPlatform] = useState<'ios'|'android'|'other'>('other');

  useEffect(() => {
    const ua = navigator.userAgent || '';

    if (campaign) {
      localStorage.setItem('campaign_code', campaign);
    }

    if (/iPhone|iPad|iPod/.test(ua)) {
      setPlatform('ios');
    } else if (/Android/.test(ua)) {
      setPlatform('android');
      const redirect = window.setTimeout(() => {
        window.location.href = `https://play.google.com/store/apps/details?id=com.letspoll.apm&referrer=${campaign}`;
      }, 1200);

      return () => window.clearTimeout(redirect);
    } else {
      setPlatform('other');
    }
  }, [campaign]);

  async function handleContinueToAppStore() {
    try {
      await navigator.clipboard.writeText(campaign);
      console.log('Campaign copied to clipboard:', campaign);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }

    window.location.href = `https://apps.apple.com/us/app/american-polling-machine/id6670273172?pt=${campaign}`;
  }

  const iosUrl = `https://apps.apple.com/us/app/american-polling-machine/id6670273172?pt=${campaign}`;
  const androidUrl = `https://play.google.com/store/apps/details?id=com.letspoll.apm&referrer=${campaign}`;
  const hasCampaign = campaign.trim().length > 0;
  const platformLabel =
    platform === 'android'
      ? 'Taking you to Google Play'
      : platform === 'ios'
        ? 'Continue on iPhone or iPad'
        : 'Download the APM app';

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 560,
          textAlign: 'center'
        }}
      >
        <Typography
          variant="h2"
          gutterBottom
          sx={{
            fontFamily: 'Montserrat',
            color: 'dodgerblue',
            fontWeight: 700
          }}
        >
          Join a Campaign
        </Typography>

        <Typography
          sx={{
            color: '#1d1d1d',
            fontSize: '1.05rem',
            lineHeight: 1.6,
            mb: 2
          }}
        >
          You were invited to answer campaign polls in the American Polling
          Machine app.
        </Typography>

        {hasCampaign && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                color: 'dodgerblue',
                fontWeight: 700,
                letterSpacing: 1,
                fontSize: '1.5rem'
              }}
            >
              {campaign.toUpperCase()}
            </Typography>
            <Typography sx={{ color: '#555', fontSize: '.9rem' }}>
              Campaign code
            </Typography>
          </Box>
        )}

        <Divider sx={{ borderColor: 'dodgerblue', mb: 2 }} />

        <Typography
          sx={{
            color: '#1d1d1d',
            fontWeight: 700,
            mb: 1
          }}
        >
          {platformLabel}
        </Typography>

        <Typography
          sx={{
            color: '#444',
            fontSize: '.95rem',
            lineHeight: 1.5,
            mb: 3
          }}
        >
          Install the app, then create an account or log in. The app will guide
          you to join this campaign before showing your group polls.
        </Typography>

        {platform === 'ios' && (
          <Stack spacing={2} alignItems="center">
            <Typography sx={{ color: '#555', fontSize: '.9rem' }}>
              We will copy the campaign code before sending you to the App Store.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinueToAppStore}
              sx={{ px: 4, py: 1.25 }}
            >
              Continue to App Store
            </Button>
          </Stack>
        )}

        {platform === 'android' && (
          <Stack spacing={2} alignItems="center">
            <Typography sx={{ color: '#555', fontSize: '.9rem' }}>
              If you are not redirected automatically, use the button below.
            </Typography>
            <Button
              component="a"
              href={androidUrl}
              variant="contained"
              color="primary"
              sx={{ px: 4, py: 1.25 }}
            >
              Open Google Play
            </Button>
          </Stack>
        )}

        {platform === 'other' && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 2,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}
          >
            <Button
              component="a"
              href={iosUrl}
              target="_blank"
              rel="noopener"
              sx={{ p: 0 }}
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                style={{ height: 60 }}
              />
            </Button>
            <Button
              component="a"
              href={androidUrl}
              target="_blank"
              rel="noopener"
              sx={{ p: 0 }}
            >
              <img
                src={playStoreBadge}
                alt="Get it on Google Play"
                style={{ height: 60 }}
              />
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
